import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { COMUNIDADES } from "@/lib/subvenciones";
import { Shirt, Footprints, Utensils, Beer, Coffee, Croissant, Scissors, Sparkles, Pill, Stethoscope, ShoppingCart, Apple, Armchair, Gem, Building2, Store, type LucideIcon } from "lucide-react";

export const RETAIL_SECTORS: { key: string; label: string; labelEn: string; Icon: LucideIcon }[] = [
  { key: "ropa", label: "Tienda de Ropa", labelEn: "Clothing Store", Icon: Shirt },
  { key: "zapateria", label: "Zapatería", labelEn: "Shoe Store", Icon: Footprints },
  { key: "restaurante", label: "Restaurante", labelEn: "Restaurant", Icon: Utensils },
  { key: "bar", label: "Bar", labelEn: "Bar", Icon: Beer },
  { key: "cafeteria", label: "Cafetería", labelEn: "Coffee Shop", Icon: Coffee },
  { key: "panaderia", label: "Panadería", labelEn: "Bakery", Icon: Croissant },
  { key: "peluqueria", label: "Peluquería", labelEn: "Hair Salon", Icon: Scissors },
  { key: "estetica", label: "Centro de Estética", labelEn: "Beauty Salon", Icon: Sparkles },
  { key: "farmacia", label: "Farmacia", labelEn: "Pharmacy", Icon: Pill },
  { key: "parafarmacia", label: "Parafarmacia", labelEn: "Health & Beauty Store", Icon: Stethoscope },
  { key: "supermercado", label: "Supermercado", labelEn: "Supermarket", Icon: ShoppingCart },
  { key: "alimentacion", label: "Tienda de Alimentación", labelEn: "Grocery Store", Icon: Apple },
  { key: "muebles", label: "Muebles y Decoración", labelEn: "Furniture & Home Decor", Icon: Armchair },
  { key: "joyeria", label: "Joyería", labelEn: "Jewelry Store", Icon: Gem },
  { key: "inmobiliaria", label: "Inmobiliaria", labelEn: "Real Estate Agency", Icon: Building2 },
  { key: "otro", label: "Otro", labelEn: "Other", Icon: Store },
] as const;

/** Sector label in the active UI language (ES by default for legacy callers). */
export const getSectorLabelFor = (sector: string, lang: "es" | "en"): string => {
  const key = LEGACY_SECTOR_ALIAS[sector] ?? sector;
  const found = RETAIL_SECTORS.find((s) => s.key === key);
  if (!found) return sector;
  return lang === "en" ? found.labelEn : found.label;
};


/** Legacy key aliases — old saved values must still resolve to a label/icon. */
const LEGACY_SECTOR_ALIAS: Record<string, string> = {
  moda: "ropa",
  restauracion: "restaurante",
  cafeterias: "cafeteria",
  hogar: "muebles",
};

/**
 * Map current UI sector keys to the legacy keys used inside
 * `module_content.sector_checklist` (admin roadmap stores checklists under:
 * moda, restauracion, cafeterias, hogar, joyeria, estetica, farmacia,
 * alimentacion, otro). Any UI sector without a direct match falls back to
 * "otro" so the public dashboard always shows the admin's sector checklist.
 */
const SECTOR_CHECKLIST_KEY_MAP: Record<string, string> = {
  ropa: "moda",
  zapateria: "moda",
  restaurante: "restauracion",
  bar: "restauracion",
  cafeteria: "cafeterias",
  panaderia: "cafeterias",
  peluqueria: "estetica",
  estetica: "estetica",
  farmacia: "farmacia",
  parafarmacia: "farmacia",
  supermercado: "alimentacion",
  alimentacion: "alimentacion",
  muebles: "hogar",
  joyeria: "joyeria",
  inmobiliaria: "otro",
  otro: "otro",
};

/** Resolve the sector key used as index inside module_content.sector_checklist. */
export const getSectorChecklistKey = (sector?: string | null): string => {
  if (!sector) return "otro";
  if (SECTOR_CHECKLIST_KEY_MAP[sector]) return SECTOR_CHECKLIST_KEY_MAP[sector];
  // Already a legacy/DB key
  if (Object.values(SECTOR_CHECKLIST_KEY_MAP).includes(sector)) return sector;
  return "otro";
};

/** Reusable sector icon component */
export const SectorIcon = ({ sector, className = "w-4 h-4" }: { sector: string; className?: string }) => {
  const key = LEGACY_SECTOR_ALIAS[sector] ?? sector;
  const found = RETAIL_SECTORS.find((s) => s.key === key);
  const IconComp = found?.Icon ?? Store;
  return <IconComp className={className} strokeWidth={1.5} />;
};

export const getSectorLabel = (sector: string, lang: "es" | "en" = "es"): string =>
  getSectorLabelFor(sector, lang);

const SECTOR_MODULE_HINTS_ES: Record<string, Record<string, string>> = {
  restauracion: {
    jugo: "Proveedores e Ingredientes",
    sabor: "Carta y Experiencia Gastronómica",
    equipo: "Sala, Cocina y Servicio",
  },
  moda: {
    jugo: "Talleres y Tejidos",
    sabor: "Colecciones y Tendencias",
    equipo: "Visual Merchandising y Ventas",
  },
  cafeterias: {
    jugo: "Proveedores y Materias Primas",
    sabor: "Carta y Maridaje",
    equipo: "Barra, Sala y Producción",
  },
  estetica: {
    jugo: "Marcas y Proveedores",
    sabor: "Servicios y Tratamientos",
    equipo: "Especialistas y Formación",
  },
  farmacia: {
    jugo: "Laboratorios y Distribuidores",
    sabor: "Dermofarmacia y OTC",
    equipo: "Atención Farmacéutica",
  },
  alimentacion: {
    jugo: "Productores y Distribución",
    sabor: "Gama de Producto y Frescura",
    equipo: "Atención al Cliente y Reposición",
  },
  joyeria: {
    jugo: "Proveedores y Piedras",
    sabor: "Colecciones y Escaparate",
    equipo: "Asesoría y Posventa",
  },
  hogar: {
    jugo: "Fabricantes y Artesanos",
    sabor: "Tendencias y Ambientación",
    equipo: "Interiorismo y Asesoría",
  },
  inmobiliaria: {
    jugo: "Cartera de Inmuebles y Captación",
    sabor: "Marketing y Escaparate Digital",
    equipo: "Comerciales y Cierre de Operaciones",
  },
};

const SECTOR_MODULE_HINTS_EN: Record<string, Record<string, string>> = {
  restauracion: {
    jugo: "Suppliers & Ingredients",
    sabor: "Menu & Dining Experience",
    equipo: "Front of House, Kitchen & Service",
  },
  moda: {
    jugo: "Workshops & Fabrics",
    sabor: "Collections & Trends",
    equipo: "Visual Merchandising & Sales",
  },
  cafeterias: {
    jugo: "Suppliers & Raw Materials",
    sabor: "Menu & Pairing",
    equipo: "Counter, Floor & Production",
  },
  estetica: {
    jugo: "Brands & Suppliers",
    sabor: "Services & Treatments",
    equipo: "Specialists & Training",
  },
  farmacia: {
    jugo: "Labs & Distributors",
    sabor: "Dermocosmetics & OTC",
    equipo: "Pharmaceutical Care",
  },
  alimentacion: {
    jugo: "Producers & Distribution",
    sabor: "Product Range & Freshness",
    equipo: "Customer Service & Restocking",
  },
  joyeria: {
    jugo: "Suppliers & Gemstones",
    sabor: "Collections & Window Display",
    equipo: "Advice & After-Sales",
  },
  hogar: {
    jugo: "Manufacturers & Artisans",
    sabor: "Trends & Styling",
    equipo: "Interior Design & Advice",
  },
  inmobiliaria: {
    jugo: "Property Portfolio & Sourcing",
    sabor: "Marketing & Digital Showcase",
    equipo: "Sales Team & Closing Deals",
  },
};

/** Sector-specific subtitle hints for module cards */
export const getSectorModuleHint = (
  sector: string,
  moduleKey: string,
  lang: "es" | "en" = "es",
): string | null => {
  const hints = lang === "en" ? SECTOR_MODULE_HINTS_EN : SECTOR_MODULE_HINTS_ES;
  return hints[sector]?.[moduleKey] ?? null;
};


interface Props {
  open: boolean;
  userId: string;
  currentCompanyName: string;
  onComplete: (companyName: string, sector: string) => void;
}

/** Países del alta. El país sustituye al antiguo selector de fiscalidad de la Calculadora. */
const ONBOARDING_COUNTRIES: { code: string; es: string; en: string }[] = [
  { code: "ES", es: "España", en: "Spain" },
  { code: "PT", es: "Portugal", en: "Portugal" },
  { code: "FR", es: "Francia", en: "France" },
  { code: "IT", es: "Italia", en: "Italy" },
  { code: "DE", es: "Alemania", en: "Germany" },
  { code: "NL", es: "Países Bajos", en: "Netherlands" },
  { code: "GB", es: "Reino Unido", en: "United Kingdom" },
  { code: "MX", es: "México", en: "Mexico" },
  { code: "AR", es: "Argentina", en: "Argentina" },
  { code: "CL", es: "Chile", en: "Chile" },
  { code: "CO", es: "Colombia", en: "Colombia" },
  { code: "US", es: "Estados Unidos", en: "United States" },
  { code: "OTRO", es: "Otro país", en: "Other country" },
];

const SectorOnboardingModal = ({ open, userId, currentCompanyName, onComplete }: Props) => {
  const { lang } = useLanguage();
  const [companyName, setCompanyName] = useState(currentCompanyName);
  const [sector, setSector] = useState("");
  const [country, setCountry] = useState("ES");
  const [comunidad, setComunidad] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!companyName.trim()) {
      toast.error(lang === "es" ? "Introduce el nombre de tu negocio." : "Enter your business name.");
      return;
    }
    if (!sector) {
      toast.error(lang === "es" ? "Selecciona tu sector comercial." : "Select your retail sector.");
      return;
    }
    if (!country) {
      toast.error(lang === "es" ? "Selecciona tu país." : "Select your country.");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("profiles").update({
      company_name: companyName.trim(),
      retail_sector: sector,
      billing_address_country: country,
      ...(country === "ES" && comunidad ? { comunidad_autonoma: comunidad } : {}),
    }).eq("user_id", userId);
    setSaving(false);
    if (error) {
      toast.error(lang === "es" ? "Error al guardar. Inténtalo de nuevo." : "Failed to save. Please try again.");
    } else {
      onComplete(companyName.trim(), sector);
    }
  };


  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-center">
            {lang === "es" ? "🍋 Personaliza tu Panel" : "🍋 Personalise your Panel"}
          </DialogTitle>
          <DialogDescription className="text-center font-body text-sm text-muted-foreground">
            {lang === "es"
              ? "Para personalizar tu experiencia y los consejos de tu mentor, cuéntanos sobre tu negocio."
              : "Tell us about your business so we can tailor your experience and your mentor's advice."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="font-body text-sm font-medium">
              {lang === "es" ? "Nombre Comercial" : "Business name"}
            </Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={lang === "es" ? "Mi Negocio, S.L." : "My Business Ltd."}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-body text-sm font-medium">
              {lang === "es" ? "Sector Comercial" : "Retail sector"}
            </Label>
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger>
                <SelectValue placeholder={lang === "es" ? "Selecciona tu sector" : "Select your sector"} />
              </SelectTrigger>
              <SelectContent>
                {RETAIL_SECTORS.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    <span className="flex items-center gap-2">
                      <s.Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                      {lang === "en" ? s.labelEn : s.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="font-body text-sm font-medium">
              {lang === "es" ? "País" : "Country"}
            </Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue placeholder={lang === "es" ? "Selecciona tu país" : "Select your country"} />
              </SelectTrigger>
              <SelectContent>
                {ONBOARDING_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {lang === "en" ? c.en : c.es}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-body text-xs text-muted-foreground">
              {lang === "es"
                ? "Adaptamos los cálculos, los impuestos y las ayudas a tu país."
                : "We adapt calculations, taxes and aid to your country."}
            </p>
          </div>
          {country === "ES" && (
            <div className="space-y-1.5">
              <Label className="font-body text-sm font-medium">
                {lang === "es" ? "Comunidad Autónoma" : "Region (Spain)"}
              </Label>
              <Select value={comunidad} onValueChange={setComunidad}>
                <SelectTrigger>
                  <SelectValue placeholder={lang === "es" ? "Selecciona tu comunidad" : "Select your region"} />
                </SelectTrigger>
                <SelectContent>
                  {COMUNIDADES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="font-body text-xs text-muted-foreground">
                {lang === "es"
                  ? "Nos sirve para avisarte de subvenciones y ayudas de tu zona."
                  : "We use it to alert you about grants and aid available in your area."}
              </p>
            </div>
          )}
          <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
            {saving
              ? (lang === "es" ? "Guardando..." : "Saving...")
              : (lang === "es" ? "Empezar ➔" : "Get started ➔")}
          </Button>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SectorOnboardingModal;
